import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  CssBaseline
} from '@mui/material';
import {
  Menu as MenuIcon,
  Map as MapIcon,
  Chat as ChatIcon,
  Gavel as GavelIcon,
  Payment as PaymentIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { clearUserSession } from '../services/api';

const drawerWidth = 280;

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, loginWithRedirect } = useAuth0();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // First, clear user session data on the backend
      await clearUserSession();
    } catch (error) {
      // Continue with logout even if session cleanup fails
      console.warn('Failed to clear backend session:', error);
    } finally {
      // Always perform Auth0 logout
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  const menuItems = [
    { text: 'Mapa de Acórdãos', icon: <MapIcon />, path: '/' },
    { text: 'Chat', icon: <ChatIcon />, path: '/chat' },
    { text: 'Defesa', icon: <GavelIcon />, path: '/defesa' },
    { text: 'Diagnóstico', icon: <BugReportIcon />, path: '/diagnostic' },
    // { text: 'Planos', icon: <PaymentIcon />, path: '/payment' }, // Temporarily hidden
  ];

  const drawer = (
    <Box
      sx={{
        height: '100%',
        backgroundColor: 'rgb(12, 19, 46)', // Match logo background color
        color: 'white'
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha('#fff', 0.12)}`,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <img 
          src="/jurismap_logo.png" 
          alt="JurisMap"
          style={{
            height: '200px',
            width: 'auto',
            objectFit: 'contain',
            display: 'block'
          }}
        />
      </Box>
      
      <List sx={{ pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            sx={{
              mx: 0,
              mb: 0,
              borderRadius: 0,
              backgroundColor: location.pathname === item.path 
                ? alpha('#fff', 0.15) 
                : 'transparent',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.1),
              },
              transition: 'all 0.2s ease'
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 600 : 400
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff', // Clean white background
          color: 'rgba(0, 0, 0, 0.87)', // Dark text
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Empty space to push user menu to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          {isAuthenticated ? (
            <Box>
              <IconButton
                size="large"
                edge="end"
                onClick={handleMenuOpen}
                sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
              >
                <Avatar
                  src={user?.picture}
                  alt={user?.name}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    border: '2px solid rgba(139, 125, 255, 0.3)'
                  }}
                />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    borderRadius: 2,
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <MenuItem onClick={handleMenuClose}>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={user?.name || 'Usuário'} />
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Configurações" />
                </MenuItem>
                <MenuItem 
                  onClick={handleLogout}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Sair" />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <IconButton
              sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
              onClick={() => loginWithRedirect()}
            >
              <AccountCircleIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default, // Clean background without gradient
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
